export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div style={{ backgroundColor: "#1B2A4A" }} className="py-10 px-6 text-white text-center">
        <h1 className="text-3xl font-bold">EdgeCare Privacy Policy</h1>
        <p className="mt-2 text-sm opacity-80">Effective Date: April 2, 2026</p>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12 text-gray-800 space-y-10 text-base leading-relaxed">

        <section>
          <p>
            EdgeCare ("we," "us," or "our") operates the EdgeCare mobile application (the "App"), a
            research platform designed to passively collect health and behavioral sensor data from
            enrolled research participants. This Privacy Policy explains what data we collect, how we
            use it, how we protect it, and your rights as a participant.
          </p>
          <p className="mt-3">
            By using the App, you acknowledge that you have read and understood this policy. If you
            have questions, contact us at the address listed at the end of this document.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            1. Who Can Use This App
          </h2>
          <p>
            The App is invite-only and restricted to individuals who have been enrolled in a
            research study by an authorized researcher. There is no public sign-up. Accounts are
            created and managed by the research team. We do not collect personally identifiable
            information (PII) such as your name, email address, or date of birth.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            2. Data We Collect
          </h2>
          <p className="mb-3">
            Once you sign in, the App continuously collects the following categories of sensor data
            from your device for research purposes:
          </p>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold">a. Location</h3>
              <p>
                Precise GPS location (foreground and background), movement patterns, and
                anonymized location clusters. This is used to understand mobility and daily
                activity patterns. Raw coordinates are aggregated into clusters and the raw
                values are not retained beyond the device.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">b. Physical Activity</h3>
              <p>
                Step counts, physical activity type (walking, running, still, in-vehicle, etc.),
                and activity recognition events detected by the device's motion sensors.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">c. Health Data (Android Health Connect)</h3>
              <p>
                With your permission, the App reads health data stored in Android Health Connect,
                including: heart rate, resting heart rate, blood pressure, blood oxygen saturation,
                respiratory rate, body temperature, weight, height, body fat percentage, sleep
                sessions, distance, active calories burned, and exercise sessions.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">d. Screen Usage</h3>
              <p>
                Screen on/off events and total daily screen-on time to understand device usage
                patterns.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">e. App Usage Statistics</h3>
              <p>
                Aggregate app usage data (time spent per app category) collected via Android's
                UsageStats API. Individual app names may be recorded to compute usage summaries.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">f. Communication Metadata</h3>
              <p>
                Call log metadata (call count, duration, type — incoming/outgoing/missed) and
                SMS count. The content of calls or messages is never accessed or stored.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">g. Bluetooth / Social Context</h3>
              <p>
                Nearby Bluetooth Low Energy (BLE) device counts and signal strength, used as a
                proxy for social proximity. Hardware identifiers are replaced on-device with
                installation-scoped pseudonyms, and advertised device names are not collected.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">h. Connectivity & Device State</h3>
              <p>
                WiFi and cellular connectivity events, battery charging state and level, and
                active notification count (number only, not content).
              </p>
            </div>

            <div>
              <h3 className="font-semibold">i. Survey Responses</h3>
              <p>
                Periodic in-app surveys asking about your mood, sleep quality, energy level, and
                social engagement. These responses are entered by you voluntarily.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            3. Data We Do NOT Collect
          </h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your name, email address, phone number, or any other PII</li>
            <li>Content of phone calls, text messages, or notifications</li>
            <li>Photos, videos, or camera input</li>
            <li>Audio recordings or microphone input</li>
            <li>Passwords or financial information</li>
            <li>Browsing history or clipboard contents</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            4. How We Use Your Data
          </h2>
          <p>All collected data is used exclusively for research purposes:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>To study behavioral and health patterns in aging populations</li>
            <li>To generate anonymized aggregate statistics and community-level comparisons shown in the App</li>
            <li>To improve the research study design and data collection methodology</li>
          </ul>
          <p className="mt-3">
            Data is never sold, rented, or used for advertising.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            5. How Data Is Stored and Secured
          </h2>
          <p>
            Data is first stored locally on your device in an encrypted SQLite database. It is then
            periodically uploaded to a secure cloud backend hosted on Amazon Web Services (AWS),
            protected by AWS Cognito authentication. All data transmission occurs over HTTPS.
            Access to the backend is restricted to authorized research personnel only.
          </p>
          <p className="mt-3">
            Data is identified by a randomly generated participant ID (not your name or phone number).
            This pseudonymous identifier is used in place of any personal identifiers throughout the
            research system.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            6. Data Sharing
          </h2>
          <p>
            We do not share your individual data with third parties. Aggregated, anonymized findings
            may be published in academic research papers or presented at conferences. No individual
            participant can be identified from published results.
          </p>
          <p className="mt-3">
            We use the following third-party infrastructure services, which process data on our behalf:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong>Amazon Web Services (AWS)</strong> — cloud storage and authentication
            </li>
            <li>
              <strong>Google Firebase</strong> — push notification delivery (used only to keep the
              background data collection service alive; no personal data is included in notifications)
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            7. Background Data Collection
          </h2>
          <p>
            The App collects data continuously in the background, including when the App is closed,
            to provide complete passive sensing coverage. A persistent notification ("EdgeCare is
            running") is always visible in your notification shade while background collection is
            active. You can stop background collection at any time by contacting your research
            coordinator to unenroll from the study.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            8. Permissions Required
          </h2>
          <p>
            The App requests the following Android permissions. Each is required for a specific
            research data collection purpose:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Location (Precise, Background)</strong> — mobility and location clustering</li>
            <li><strong>Physical Activity</strong> — step counting and activity recognition</li>
            <li><strong>Health Connect</strong> — reading health metrics</li>
            <li><strong>Usage Stats</strong> — app usage summaries</li>
            <li><strong>Call Log</strong> — call metadata (not content)</li>
            <li><strong>Read SMS</strong> — SMS count (not content)</li>
            <li><strong>Post Notifications</strong> — foreground service notification</li>
            <li><strong>Boot Completed</strong> — restart the background service after device reboot</li>
            <li><strong>Battery Optimization Exemption</strong> — prevent OS from killing background service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            9. Data Retention
          </h2>
          <p>
            Data is retained for the duration of the research study and for a period necessary to
            complete analysis and publish findings, typically no longer than 5 years after study
            completion. Upon study conclusion, your data will be fully anonymized or securely deleted
            per your study agreement.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            10. Your Rights
          </h2>
          <p>As a research participant, you have the right to:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Withdraw from the study at any time by contacting your research coordinator</li>
            <li>Request a copy of the data associated with your participant ID</li>
            <li>Request deletion of your data from our servers</li>
            <li>Revoke any permission granted to the App through your device's system settings</li>
          </ul>
          <p className="mt-3">
            Note: Revoking permissions may prevent the App from functioning correctly or collecting
            complete data for the study.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            11. Children's Privacy
          </h2>
          <p>
            This App is intended for adult research participants only. We do not knowingly collect
            data from individuals under the age of 18.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            12. Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy as the study evolves. The "Effective Date" at the top
            of this page will reflect the most recent revision. Continued use of the App after
            changes are posted constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3" style={{ color: "#1B2A4A" }}>
            13. Contact Us
          </h2>
          <p>
            If you have questions or concerns about this Privacy Policy or how your data is handled,
            please contact the EdgeCare research team:
          </p>
          <div className="mt-3 p-4 rounded-lg" style={{ backgroundColor: "#F5F7FA" }}>
            <p className="font-semibold">EdgeCare Research Team</p>
            <p className="mt-1">
              Email:{" "}
              <a
                href="mailto:me@ashwanthbk.com"
                style={{ color: "#2D4A7A" }}
                className="underline"
              >
                me@ashwanthbk.com
              </a>
            </p>
          </div>
        </section>

        <hr className="border-gray-200" />
        <p className="text-sm text-gray-500 text-center">
          &copy; {new Date().getFullYear()} EdgeCare Research Project. All rights reserved.
        </p>
      </div>
    </div>
  );
}
